import { useRouter } from "next/router";
import { Dispatch, SetStateAction, useEffect } from "react";

import useTranslatorVisibility from "@/assignments/useTranslatorVisibility/useTranslatorVisibility";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import useHasFeature from "@/features/useHasFeature";
import { Assignment } from "@/types/models/Assignment.interface";
import { ContentLevel } from "@/types/models/ContentLevel.interface";
import { User } from "@/types/models/User.interface";
import getContentLabel from "@/utils/getContentLabel";
import articleTypes, { ArticleType } from "@/view/constants/articleTypes";
import contentTypes, { ContentType } from "@/view/constants/contentTypes";
import uiTypes from "@/view/constants/uiTypes";
import AddAction from "@/view/SecondaryActions/AddAction";
import CitationAction from "@/view/SecondaryActions/CitationAction";
import FontSizeAction from "@/view/SecondaryActions/FontSizeAction";
import HideAction from "@/view/SecondaryActions/HideAction";
import ParagraphCountAction from "@/view/SecondaryActions/ParagraphCountAction";
import PresentAction from "@/view/SecondaryActions/PresentAction";
import ReadAction from "@/view/SecondaryActions/ReadAction";
import SaveAction from "@/view/SecondaryActions/SaveAction";
import ShareAction from "@/view/SecondaryActions/ShareAction";
import TranslateAction from "@/view/SecondaryActions/TranslateAction";
import {
  getSelectedAssignment,
  isReadActionVisible,
} from "@/view/utils/SecondaryActions/SecondaryActions";
import useAccessibilityControlsContext from "@/view/utils/useAccessibilityControlsContext";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import useUserContentUIContext from "@/view/utils/useUserContentUIContext";
import useTranslatorControlsContext from "../utils/useTranslatorControlsContext";
import { IArticlePublications } from "./CitationAction/CitationAction.interface";
import { VIEW_PAGE_TRANSLATOR_FEATURE } from "./TranslateAction/constants";

export type DISABLED_FEATURES =
  | "add-to-text-set"
  | "listen-mode"
  | "paragraph-count"
  | "present-mode"
  | "translation";

interface SecondaryActionsProps {
  activeSubMenu?: string | null;
  bannerMode: {
    toggleBannerMode: () => void;
    canUseBannerMode: boolean;
  };
  content: {
    id: string;
    content_type: ContentType;
    data?: {
      article_type: ArticleType;
      author?: string;
      content_id: string;
      display_published_at?: string;
      thumbnail: string;
      link_title?: string;
      article_publications?: IArticlePublications[];
    };
  };
  currentLevel?: ContentLevel | undefined;
  isMenuAction?: boolean;
  isMobile?: boolean;
  matchedAssignments: Assignment[];
  readAloudButtonRef?: object | undefined;
  setActiveSubMenu?: Dispatch<SetStateAction<null>>;
  user: User;
  userProperties: {
    isTeacher: boolean;
    isParent: boolean;
    isStudent: boolean;
  }; // there is another PR adding types for userProperties
  disabledFeatures?: DISABLED_FEATURES[];
}

const SecondaryActions = ({
  activeSubMenu,
  bannerMode,
  content,
  currentLevel,
  isMenuAction = false,
  isMobile,
  matchedAssignments,
  readAloudButtonRef,
  setActiveSubMenu,
  user,
  userProperties,
  disabledFeatures = [],
}: SecondaryActionsProps) => {
  const { toggleBannerMode, canUseBannerMode } = bannerMode;
  const { isTeacher, isParent, isStudent } = userProperties;
  const {
    id: contentId,
    content_type: contentType,
    data: contentData,
  } = content;

  const contentIsVideo = contentType === contentTypes.INTERACTIVE_VIDEO;
  const contentIsPoem =
    contentType === contentTypes.ARTICLE &&
    contentData?.article_type === articleTypes.ARTICLE_POEM;

  const router = useRouter();
  const { selectedAssignmentId = "" } = router.query;

  const { isTranslateMode, setIsTranslateMode } =
    useTranslatorControlsContext();
  const {
    currentFontSize,
    currentFontSizeIndicator,
    setCurrentFontSize,
    showParagraphCount,
    setShowParagraphCount,
  } = useAccessibilityControlsContext();
  const { isStudentPreviewOn } = useStudentPreviewContext();

  const [citationFeatureEnabled] =
    useGetUserExperimentSegment("citation_feature");
  const [hasParagraphCountFeature] = useHasFeature("paragraph_count_feature");
  const [hasFontSizeFeature] = useHasFeature("font_size_feature");
  const [hasVideoPresentModeFeature] = useHasFeature("video_present_mode");
  const [hasTranslateFeature] = useHasFeature(VIEW_PAGE_TRANSLATOR_FEATURE);
  const { uiType } = useUserContentUIContext();

  const liteUserPremiumContent = uiType === uiTypes.LITE_USER_PREMIUM_CONTENT;
  const liteUserLiteContent = uiType === uiTypes.LITE;
  const liteStudentLiteContent = isStudent && liteUserLiteContent;

  const disablePremiumActions = liteUserPremiumContent && !isStudent;

  const selectedAssignment = getSelectedAssignment(
    matchedAssignments,
    selectedAssignmentId.toString(),
  );

  const { isTranslationActionVisible, isTranslateActionUnavailable } =
    useTranslatorVisibility({
      content: {
        content_type: content?.content_type as ContentType,
        data: content?.data,
      },
      language: currentLevel?.language,
      selectedAssignment,
    });

  const showReadAction =
    isReadActionVisible(
      isStudent || isStudentPreviewOn,
      contentIsVideo,
      selectedAssignment,
    ) &&
    !isMenuAction &&
    !disabledFeatures.includes("listen-mode");

  const showTranslateAction =
    isTranslationActionVisible &&
    !isMenuAction &&
    !disabledFeatures.includes("translation");

  const showShareAction = !isMenuAction;
  const showHideAction = isTeacher && !isStudentPreviewOn;
  const showAddAction =
    (isTeacher || isParent) &&
    !isStudentPreviewOn &&
    !disabledFeatures.includes("add-to-text-set");
  const showCitationAction = citationFeatureEnabled === "on" && !isMenuAction;
  const showPresentActionForVideo =
    canUseBannerMode && hasVideoPresentModeFeature && !isStudentPreviewOn;
  const showPresentAction = contentIsVideo
    ? showPresentActionForVideo && !disabledFeatures.includes("present-mode")
    : canUseBannerMode &&
      !isStudentPreviewOn &&
      !disabledFeatures.includes("present-mode");
  const showFontSizeAction =
    hasFontSizeFeature && !contentIsVideo && !contentIsPoem;

  const isContentEligibleForParagraphCount =
    hasParagraphCountFeature && !contentIsVideo && !contentIsPoem;

  const showToolbarParagraphCount =
    isContentEligibleForParagraphCount &&
    !isMenuAction &&
    (!isMobile || !hasTranslateFeature);

  const showDropdownParagraphCount =
    isContentEligibleForParagraphCount &&
    hasTranslateFeature &&
    Boolean(isMobile) &&
    !!isMenuAction;

  const showSaveAction = !liteStudentLiteContent && !isMenuAction;

  const showParagraphCountAction =
    !disabledFeatures.includes("paragraph-count");

  const fontSizeIndicator: string = currentFontSizeIndicator;
  const fontSizeLabel =
    "Text size" + (currentFontSizeIndicator ? `: ${fontSizeIndicator}` : "");

  const contentTitle = contentIsVideo
    ? contentData?.link_title
    : currentLevel?.title;
  const currentLevelId = contentIsVideo ? contentId : currentLevel?.id;
  const contentTypeLabel = getContentLabel({
    content_type: contentType,
    label: "",
  });

  useEffect(() => {
    if (showParagraphCount) setIsTranslateMode(false);
  }, [showParagraphCount]);

  useEffect(() => {
    if (isTranslateMode) setShowParagraphCount(false);
  }, [isTranslateMode]);

  return (
    <>
      {showShareAction && (
        <ShareAction
          contentId={contentId}
          contentType={contentTypeLabel}
          title={contentTitle}
          currentLevelId={currentLevelId}
          hoverLabel="Share"
          isMobile={isMobile}
          thumbnail={content?.data?.thumbnail || ""}
        />
      )}
      {showSaveAction && (
        <SaveAction
          activeSubMenu={activeSubMenu}
          contentId={contentData?.content_id ?? contentId}
          currentLevel={currentLevel}
          hoverLabel="Save"
          isMobile={isMobile}
        />
      )}
      {showTranslateAction && (
        <TranslateAction
          activeSubMenu={activeSubMenu}
          currentLevel={currentLevel}
          disabled={disablePremiumActions}
          hoverLabel={
            isTranslateActionUnavailable && !disablePremiumActions
              ? "Translations are not available on this article"
              : "Translate"
          }
          isMobile={isMobile}
          isTranslateActionUnavailable={isTranslateActionUnavailable}
        />
      )}
      {showParagraphCountAction && (
        <ParagraphCountAction
          activeSubMenu={activeSubMenu}
          currentLevel={currentLevel}
          disabled={disablePremiumActions}
          hoverLabel="Count"
          isMobile={isMobile}
          showToolbarParagraphCount={showToolbarParagraphCount}
          showDropdownParagraphCount={showDropdownParagraphCount}
        />
      )}
      {showReadAction && (
        <ReadAction
          activeSubMenu={activeSubMenu}
          currentLevel={currentLevel}
          disabled={disablePremiumActions}
          hoverLabel="Listen"
          isMobile={isMobile}
          readAloudButtonRef={readAloudButtonRef}
        />
      )}
      {showPresentAction && (!isMobile || isMenuAction) && (
        <PresentAction
          activeSubMenu={activeSubMenu}
          content={content}
          disabled={disablePremiumActions}
          handleToggleBannerMode={toggleBannerMode}
          hoverLabel="Present"
          isMobile={isMobile}
          setActiveSubMenu={setActiveSubMenu}
        />
      )}
      {showHideAction && (!isMobile || isMenuAction) && (
        <HideAction
          activeSubMenu={activeSubMenu}
          contentId={contentId}
          currentLevel={currentLevel}
          disabled={disablePremiumActions}
          hoverLabel="Hide"
          isMobile={isMobile}
          matchedAssignments={matchedAssignments}
          setActiveSubMenu={setActiveSubMenu}
        />
      )}
      {showAddAction && (!isMobile || isMenuAction) && (
        <AddAction
          activeSubMenu={activeSubMenu}
          contentId={contentId}
          contentType={contentType}
          currentLevel={currentLevel}
          disabled={disablePremiumActions || liteUserLiteContent}
          hoverLabel="Add"
          isMobile={isMobile}
          setActiveSubMenu={setActiveSubMenu}
          showLockIcon={liteUserLiteContent}
          user={user}
        />
      )}
      {showFontSizeAction && (!isMobile || isMenuAction) && (
        <FontSizeAction
          activeSubMenu={activeSubMenu}
          contentId={currentLevel?.id}
          currentFontSize={currentFontSize}
          disabled={disablePremiumActions}
          hoverLabel={fontSizeLabel}
          isMobile={isMobile}
          setActiveSubMenu={setActiveSubMenu}
          setCurrentFontSize={setCurrentFontSize}
        />
      )}
      {showCitationAction && (
        <CitationAction
          contentData={contentData}
          disabled={false}
          currentLevel={currentLevel}
          hoverLabel="Citation"
        />
      )}
    </>
  );
};

export default SecondaryActions;
